/**
 * The customer's sites — the screen they land on after signing in.
 *
 * ⚠️ IT PICKS UP A DESCRIPTION LEFT BEHIND BEFORE SIGN-IN. Somebody types what their business does
 * on the landing page, is sent to the identity provider, and comes back here; handing them an empty
 * screen at that moment loses exactly the people who were furthest along.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Button, Card, Chip, EmptyState, Field, Input, Notice, Select, Spinner, Textarea, useLang } from '@ksa/ui'
import { isHandle } from '@ksa/model'
import { ApiFailure, api, isLive, publishedUrl, snapshotIsCurrent, trialDaysLeft, type SiteSummary } from '../api.ts'
import { destinationFor } from '../generated.ts'
import { useLoad } from '../useLoad.ts'
import { useTitle } from '../useTitle.ts'

/**
 * ⚠️ THIS NUMBER IS ALSO IN `apps/api/src/routes/sites.ts` AS `DRAFT_TTL_MS`, AND THEY MUST AGREE.
 * The API cannot be imported here — `packages/*` may not depend on `apps/*` and neither may another
 * app — so this is a genuine second copy, which §0.5 says is a smell. It is accepted because the
 * alternative is a route that exists only to tell the browser a constant, and it is called out in
 * both places so that changing one without the other is a deliberate act rather than an oversight.
 * What it protects: a customer told "thirty days" whose draft vanishes on the fourteenth.
 */
const DRAFT_DAYS = 30

const STARTERS = [
  { value: 'blank', en: 'Start empty', ar: 'ابدأ فارغًا' },
  { value: 'restaurant', en: 'Restaurant or café', ar: 'مطعم أو مقهى' },
  { value: 'services', en: 'Services business', ar: 'نشاط خدمي' },
  { value: 'shop', en: 'Shop', ar: 'متجر' },
  { value: 'clinic', en: 'Clinic or practice', ar: 'عيادة أو مركز' },
  { value: 'portfolio', en: 'Portfolio', ar: 'أعمالي' },
]

export function Sites() {
  const { t, lang } = useLang()
  useTitle('Your sites', 'مواقعك')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [sites, reload] = useLoad((signal) => api.sites(signal), [])
  const [creating, setCreating] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  // The description the landing page stashed before sending them to sign in.
  useEffect(() => {
    if (params.get('build') !== '1') return
    let pending = ''
    try {
      pending = sessionStorage.getItem('ksa:pending-description') ?? ''
      sessionStorage.removeItem('ksa:pending-description')
    } catch {
      /* storage disabled */
    }
    setParams(new URLSearchParams(), { replace: true })
    if (pending.trim().length < 10) return

    setGenerating(true)
    api.generate(pending, lang).then(
      // Signed in by construction on this screen, so this is `/s/<id>` — but the branch belongs in
      // one place rather than being assumed at each call site. See `generated.ts`.
      (created) => navigate(destinationFor(created)),
      (error: unknown) => {
        setFailure(error instanceof ApiFailure ? error : null)
        setGenerating(false)
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (generating) {
    return (
      <div className="u-page u-section u-stack" style={{ textAlign: 'center', alignItems: 'center' }}>
        <Spinner label={t('Building your website', 'جارٍ بناء موقعك')} />
        <h1 className="u-title">{t('Building your website', 'جارٍ بناء موقعك')}</h1>
        <p className="u-lead">
          {t(
            'Writing the pages in both languages. This takes about half a minute.',
            'نكتب الصفحات باللغتين. يستغرق هذا نصف دقيقة تقريبًا.',
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="u-page u-section u-stack--loose u-stack">
      <div className="u-row u-row--between">
        <h1 className="u-title">{t('Your sites', 'مواقعك')}</h1>
        <Button onClick={() => setCreating(true)}>{t('New site', 'موقع جديد')}</Button>
      </div>

      {failure && <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>}

      {creating && (
        <NewSiteForm
          onCancel={() => setCreating(false)}
          onCreated={(where) => navigate(where)}
        />
      )}

      {sites.state === 'loading' && <Spinner label={t('Loading your sites', 'جارٍ تحميل مواقعك')} />}

      {sites.state === 'error' && (
        <Notice tone="error">
          {lang === 'ar' ? sites.failure.text.ar : sites.failure.text.en}
          {sites.failure.code === 'auth.required' && (
            <>
              {' '}
              <a className="u-link" href="/auth/start?returnTo=/sites">
                {t('Sign in', 'تسجيل الدخول')}
              </a>
            </>
          )}
        </Notice>
      )}

      {sites.state === 'ready' && sites.data.length === 0 && !creating && (
        <EmptyState
          title={t('Nothing here yet', 'لا يوجد شيء بعد')}
          body={t(
            'Describe your business and we will write the first draft — or start from a template and fill it in yourself.',
            'صِف نشاطك وسنكتب المسودة الأولى — أو ابدأ من قالب واملأه بنفسك.',
          )}
          action={<Button onClick={() => setCreating(true)}>{t('Create your first site', 'أنشئ موقعك الأول')}</Button>}
        />
      )}

      {sites.state === 'ready' && sites.data.length > 0 && (
        <div className="u-grid">
          {sites.data.map((site) => (
            <SiteCard key={site.id} site={site} onChanged={reload} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * How long this draft has left, and what resets it.
 *
 * ⚠️ IT COUNTS FROM `updatedAt`, THE SAME FIELD THE SWEEP READS. Two different notions of "untouched"
 * — one on screen and one in the job — is how a customer gets told they have a week and loses it
 * tomorrow.
 */
function DraftExpiry({ updatedAt }: { updatedAt: string }) {
  const { t, lang } = useLang()
  const days = Math.ceil((new Date(updatedAt).getTime() + DRAFT_DAYS * 86_400_000 - Date.now()) / 86_400_000)

  // Nothing alarming until it is nearly gone; a fresh draft does not need a countdown on it.
  const soon = days <= 7
  const number = new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-GB', { numberingSystem: 'latn' }).format(
    Math.max(0, days),
  )

  return (
    <p className={`u-tiny ${soon ? '' : 'u-faint'}`} style={soon ? { color: 'var(--u-amber-ink)' } : undefined}>
      {soon
        ? t(
            `Unfinished drafts are removed after ${DRAFT_DAYS} days. This one has ${number} left — open it to keep it.`,
            `تُحذف المسودات غير المكتملة بعد ${DRAFT_DAYS} يومًا. بقي لهذه ${number} — افتحها للاحتفاظ بها.`,
          )
        : t(
            `Unfinished drafts are removed after ${DRAFT_DAYS} days without changes.`,
            `تُحذف المسودات غير المكتملة بعد ${DRAFT_DAYS} يومًا دون تغيير.`,
          )}
    </p>
  )
}

function SiteCard({ site, onChanged }: { site: SiteSummary; onChanged: () => void }) {
  const { t, lang } = useLang()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  /**
   * ⚠️ `isLive`, NOT `Boolean(site.published)`. Publishing is free for a week and the sweep takes an
   * unpaid site off the internet at the end of it, which leaves a record that HAS published and is
   * not reachable — the exact case a customer most needs told about, and the one the old truthiness
   * check got wrong.
   */
  const live = isLive(site) && site.subscription?.status !== 'cancelled'

  /** Whole days of the free week left, or `null` for a paid site and for one never published. */
  const daysLeft = trialDaysLeft(site)

  /** Published once, and taken down when nobody paid. */
  const takenDown = Boolean(site.published?.takenDownAt)

  /**
   * ⚠️ IT GOES STRAIGHT TO THE PAYMENT, WHICH IS THE ONE PLACE IN THIS PRODUCT THAT IS ALLOWED TO.
   * Everywhere else a primary button is a route to a screen with something to read (§5.7) — but
   * somebody pressing «Keep it online» has already read the countdown beside the button and made the
   * decision; the screen they arrive at is the checkout, which is where the price is.
   */
  const keepOnline = async () => {
    setBusy(true)
    try {
      const started = await api.subscribe(site.id, '/sites')
      if (started.payUrl) {
        window.location.href = started.payUrl
        return
      }
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  /**
   * Whether this card should be asking to be published.
   *
   * ⚠️ A LIVE SITE WITH LATER EDITS STILL NEEDS IT, and that is the case a plain `!live` would miss.
   * Publishing writes an immutable snapshot (§2.2); editing afterwards changes the document and not
   * the snapshot, so a customer can fix a phone number, see it in the editor, and never learn that
   * the page a visitor loads still has the old one.
   *
   * ⚠️ AND THE WORDS DIFFER, because the acts differ. "Publish" is a first time — an address and a
   * decision. "Publish changes" is a re-run of something already agreed.
   */
  const stale = live && !snapshotIsCurrent(site)

  /**
   * ⚠️ THE COUNTDOWN IS ON THE CARD, EVERY DAY, WHICH IS THE SAME PROMISE THE DRAFT EXPIRY MAKES.
   * We have no email address to warn anybody with, so the screen that shows a site is the only place
   * this can be said — and a site that vanishes from the internet with no warning is worse than one
   * that was never published, because somebody has already sent the link to a customer.
   */
  const countdown =
    daysLeft === null
      ? null
      : daysLeft === 0
        ? t('Coming down today unless you keep it.', 'سيُزال اليوم ما لم تحتفظ به.')
        : daysLeft === 1
          ? t('One day left of the free week.', 'يوم واحد متبقٍ من الأسبوع المجاني.')
          : t(`${daysLeft} days left of the free week.`, `${daysLeft} أيام متبقية من الأسبوع المجاني.`)

  return (
    <Card>
      <div className="u-stack">
        <div className="u-row u-row--between">
          <h2 className="u-heading">{site.name}</h2>
          {live ? (
            <Chip tone="live">{t('Live', 'منشور')}</Chip>
          ) : takenDown ? (
            /* ⚠️ ITS OWN WORD, NOT «Draft». A site that was on the internet and is not any more is a
               different situation from one that never went up, and the difference is what the
               customer has to act on. */
            <Chip tone="attention">{t('Taken down', 'أُزيل')}</Chip>
          ) : site.subscription?.status === 'pending' ? (
            <Chip tone="attention">{t('Payment pending', 'بانتظار الدفع')}</Chip>
          ) : (
            <Chip tone="draft">{t('Draft', 'مسودة')}</Chip>
          )}
        </div>

        {/*
          ⚠️ A DRAFT HAS NO ADDRESS AND SAYS SO. It used to show one because creating a site claimed
          one; now building reserves nothing, and printing a proposal here would be showing somebody
          a name that is not theirs and that somebody else may take.
        */}
        {site.handle ? (
          <a
            className="u-num u-small"
            href={publishedUrl(site.handle)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: live ? 'var(--u-green)' : 'var(--u-ink-faint)' }}
          >
            <span className="u-hostname">{site.handle}.ksa-web.app</span>
          </a>
        ) : (
          <span className="u-small u-faint">
            {t('No address yet — you choose one when you publish.', 'لا يوجد عنوان بعد — تختاره عند النشر.')}
          </span>
        )}

        {/*
          ⚠️ THE EXPIRY IS STATED ON EVERY DRAFT, EVERY TIME, AND THAT IS WHAT MAKES DELETING THEM
          DEFENSIBLE. Drafts are swept after thirty days without a change (see `DRAFT_TTL_MS`), and we
          have no email address to warn anybody with — so this screen is the only warning there will
          ever be. A note in terms nobody reads is not a warning; a sentence on the card, beside the
          date it is counted from, is.
          ⚠️ AND IT SAYS HOW TO STOP IT. "Will be deleted" is a threat; "opening it resets the clock"
          is something the customer can act on.
        */}
        {!site.published && !site.subscription && <DraftExpiry updatedAt={site.updatedAt} />}

        {/*
          ⚠️ THE FREE WEEK COUNTS DOWN HERE, AND KEEPING IT IS THE THING TO PRESS. This is the
          product's only upsell and it lives on the card rather than in the way of anything: the
          customer sees it every time they look at their sites, and never in the middle of an edit.
        */}
        {countdown && (
          <Notice tone="warn" title={countdown}>
            <div className="u-row" style={{ marginBlockStart: 'var(--u-s2)' }}>
              <Button size="sm" busy={busy} onClick={() => void keepOnline()}>
                {t('Keep it online', 'أبقِه منشورًا')}
              </Button>
            </div>
          </Notice>
        )}

        {/*
          ⚠️ A TAKEN-DOWN SITE SAYS WHAT HAPPENED AND WHAT IS LEFT, in that order. "Nothing is
          deleted" is the sentence that stops somebody assuming their work is gone and starting again
          — and the thirty days is the same promise the draft expiry makes, because a taken-down site
          IS a draft again as far as the sweep is concerned.
        */}
        {takenDown && (
          <Notice tone="warn" title={t('This site is not on the internet', 'هذا الموقع غير منشور')}>
            {t(
              'The free week ended. Nothing is deleted — keep it and publish again, and it comes straight back at the same address.',
              'انتهى الأسبوع المجاني. لم يُحذف شيء — احتفظ به وانشره مجددًا ليعود فورًا على العنوان نفسه.',
            )}
            <div className="u-row" style={{ marginBlockStart: 'var(--u-s2)' }}>
              <Button size="sm" busy={busy} onClick={() => void keepOnline()}>
                {t('Keep it online', 'أبقِه منشورًا')}
              </Button>
            </div>
          </Notice>
        )}

        {site.subscription?.status === 'past_due' && (
          <Notice tone="warn">
            {t(
              'Your site is still online, but the last payment did not go through.',
              'موقعك ما زال يعمل، لكن آخر عملية دفع لم تنجح.',
            )}
          </Notice>
        )}

        <p className="u-tiny u-faint">
          {t('Last changed ', 'آخر تعديل ')}
          {new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-GB', {
            dateStyle: 'medium',
            numberingSystem: 'latn',
          }).format(new Date(site.updatedAt))}
        </p>

        <div className="u-row">
          {/*
            ⚠️ THE PUBLISH BUTTON IS FIRST AND IT IS THE PRIMARY ONE, because this is the act the
            product is FOR. It was reachable only by opening the site, switching to a pane whose menu
            says «Preview», and scrolling past four groups — so the step this whole product exists to
            reach was the least discoverable thing on the screen.

            ⚠️ IT IS A LINK TO THE WIZARD, NOT A PUBLISH. The checks, the address and what happens
            when the free week ends are things to read; a card that put a site on the internet in one
            tap would be making that decision for somebody.
          */}
          {(!live || stale) && (
            <Link to={`/s/${site.id}?publish=1`}>
              <Button size="sm">
                {/* ⚠️ THREE WORDS FOR THREE ACTS. A first publish is an address and a decision;
                    «Publish changes» re-runs something already agreed; and putting back a site the
                    sweep took down is neither, which is why it says so. */}
                {takenDown
                  ? t('Publish it again', 'انشره مجددًا')
                  : stale
                    ? t('Publish changes', 'انشر التغييرات')
                    : t('Publish', 'انشر')}
              </Button>
            </Link>
          )}

          <Link to={`/s/${site.id}`}>
            <Button size="sm" variant={!live || stale ? 'quiet' : undefined}>
              {t('Edit', 'تعديل')}
            </Button>
          </Link>

          {/*
            ⚠️ TWO DIFFERENT WORDS FOR TWO DIFFERENT THINGS, NOT ONE WORD FOR BOTH. "Show" opens the
            site as the public sees it, on the customer's own address. "Preview" opens a draft that
            nobody else can reach — the same pages, rendered by the same renderer, at a URL that
            requires the session. Calling both of them "View" would let somebody send a friend a link
            to a site that is not published and does not answer for anybody but them.
          */}
          {live && site.handle ? (
            <a href={publishedUrl(site.handle)} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="quiet">
                {t('Show', 'عرض')}
              </Button>
            </a>
          ) : (
            <a href={`/api/sites/${encodeURIComponent(site.id)}/preview/`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="quiet">
                {t('Preview', 'معاينة')}
              </Button>
            </a>
          )}

          <div className="u-spacer" />
          <Button variant="ghost" size="sm" onClick={() => setConfirming((c) => !c)}>
            {t('Delete', 'حذف')}
          </Button>
        </div>

        {confirming && (
          <Notice tone="warn" title={t('Delete this site?', 'حذف هذا الموقع؟')}>
            {/*
              ⚠️ THE CONSEQUENCE IS STATED BEFORE THE CLICK, NOT AFTER IT — AND IT IS NOW A DIFFERENT
              CONSEQUENCE FOR A DRAFT. This said the address would be released and somebody else
              could take it, which was true when creating a site claimed one. A draft holds none, so
              saying it anyway would be inventing a stake to make the confirmation feel weightier —
              which is precisely how people learn to click through confirmations.
            */}
            {site.handle ? (
              <p>
                {t('The address ', 'سيتحرّر العنوان ')}
                <strong className="u-num u-hostname">{site.handle}.ksa-web.app</strong>
                {t(
                  ' is released and someone else can take it. This cannot be undone.',
                  ' ويمكن لشخص آخر أخذه. لا يمكن التراجع عن هذا.',
                )}
              </p>
            ) : (
              <p>
                {t(
                  'Everything you have written for this site goes with it. This cannot be undone.',
                  'كل ما كتبته لهذا الموقع سيُحذف معه. لا يمكن التراجع عن هذا.',
                )}
              </p>
            )}
            <div className="u-row" style={{ marginBlockStart: 'var(--u-s3)' }}>
              <Button
                variant="danger"
                size="sm"
                busy={busy}
                onClick={async () => {
                  setBusy(true)
                  await api.deleteSite(site.id).catch(() => undefined)
                  setBusy(false)
                  setConfirming(false)
                  onChanged()
                }}
              >
                {t('Delete it', 'احذفه')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                {t('Keep it', 'احتفظ به')}
              </Button>
            </div>
          </Notice>
        )}
      </div>
    </Card>
  )
}

/**
 * Making a site: the same one question the landing page asks.
 *
 * ⚠️ THE NAME, THE ADDRESS AND THE TEMPLATE PICKER ARE ALL GONE. This was a form of four fields
 * standing between a customer and the thing they came for, every one of them a decision they were
 * being asked to make BEFORE seeing anything: an address that is permanent, a name they cannot yet
 * change, a template they cannot yet picture. The landing page asks one question and builds a whole
 * site from it; there is no reason the second site should be harder than the first.
 *
 * The blank path survives as one quiet button, because somebody who wants to build by hand should
 * not have to describe a business to a model first.
 *
 * > ⚠️ **THERE IS STILL NO WAY TO RENAME A SITE**, and removing the name field makes that more
 * > visible rather than causing it: a blank site is "Untitled site" on the card until a rename
 * > exists. Generated sites take their name from the model's title, which is the common path.
 */
/**
 * ⚠️ THE FORM REPORTS WHERE TO GO, NOT WHAT WAS MADE. Its two paths no longer end in the same kind of
 * thing — a starter always produces a stored site, and a generation may produce a document this tab
 * is holding — so a callback taking an id would have to be lied to by one of them. `generated.ts`
 * owns that branch; this screen only navigates.
 */
function NewSiteForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (where: string) => void }) {
  const { t, lang } = useLang()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const blank = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const created = await api.createSite({ name: t('Untitled site', 'موقع بلا اسم'), starter: 'blank' })
      onCreated(`/s/${created.id}`)
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
      setBusy(false)
    }
  }

  return (
    <Card raised>
      <div className="u-stack">
        <h2 className="u-heading">{t('New site', 'موقع جديد')}</h2>

        <BuildWithAi onCreated={onCreated} />

        {failure && <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>}

        <div className="u-row">
          <Button type="button" variant="quiet" size="sm" busy={busy} onClick={() => void blank()}>
            {t('Start from a blank page instead', 'ابدأ من صفحة فارغة بدلًا من ذلك')}
          </Button>
          <div className="u-spacer" />
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('Cancel', 'إلغاء')}
          </Button>
        </div>
      </div>
    </Card>
  )
}

/**
 * Build a site from a description, from inside the admin area.
 *
 * ⚠️ THIS EXISTED ONLY ON THE LANDING PAGE, WHICH MADE THE PRODUCT'S HEADLINE FEATURE REACHABLE
 * EXACTLY ONCE — before signing in. The landing page stashes a description in `sessionStorage`, sends
 * the visitor through sign-in, and `/sites?build=1` picks it up on the way back. A customer who is
 * already signed in and wants a second site never passes through that page, so they were offered the
 * template dropdown and nothing else. The AI is what they came for and what they are paying for.
 *
 * ⚠️ NOT A NESTED `<form>`. It renders INSIDE the template form's `<form>`, and a form inside a form
 * is invalid HTML that browsers resolve by silently dropping the inner one — which would make this
 * button submit the template create instead.
 *
 * ⚠️ AND IT DISAPPEARS WHEN THERE IS NO KEY, rather than failing when pressed. `/ai/available` is the
 * server's own answer about its own configuration (CLAUDE.md §0.4: the claim belongs to whatever
 * knows), so a deployment with no `OPENAI_API_KEY` offers templates and says so, instead of offering
 * a button that spends thirty seconds arriving at an error.
 */
function BuildWithAi({ onCreated }: { onCreated: (where: string) => void }) {
  const { t, lang } = useLang()
  const [ai] = useLoad((signal) => api.aiAvailable(signal), [])
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  if (ai.state === 'ready' && !ai.data.available) {
    return (
      <Notice>
        {t(
          'Building from a description is not switched on here yet. Start from a template below.',
          'الإنشاء من وصف غير مُفعّل هنا بعد. ابدأ من قالب بالأسفل.',
        )}
      </Notice>
    )
  }
  if (ai.state !== 'ready') return null

  const build = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const created = await api.generate(description, lang)
      onCreated(destinationFor(created))
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
      setBusy(false)
    }
  }

  return (
    <div className="u-stack">
      <Field
        label={t('Describe your business', 'صف نشاطك')}
        hint={t(
          'A sentence or two. What you sell, who buys it, where you are.',
          'جملة أو جملتان. ماذا تبيع، ومن يشتري، وأين أنت.',
        )}
      >
        {(a) => (
          <Textarea
            {...a}
            rows={3}
            value={description}
            placeholder={t(
              'We roast coffee in Riyadh and sell it online and in one shop in Al Olaya.',
              'نحمّص القهوة في الرياض ونبيعها أونلاين وفي محل واحد بحي العليا.',
            )}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>

      {failure && <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>}

      <div className="u-row">
        {/* `type="button"` is this design system's default — see components.tsx — which is what keeps
            this out of the surrounding template form's submit. */}
        <Button busy={busy} disabled={description.trim().length < 10} onClick={() => void build()}>
          {t('Build my website', 'ابنِ موقعي')}
        </Button>
        {busy && (
          <span className="u-small u-faint">{t('This takes about half a minute.', 'يستغرق هذا نحو نصف دقيقة.')}</span>
        )}
      </div>
    </div>
  )
}

/*
 * ⚠️ `suggest()` USED TO LIVE HERE AND IS DELETED RATHER THAN KEPT AS A FALLBACK. It stripped
 * everything outside `a-z0-9`, so «مقهى العقيق» suggested nothing at all; briefly it used `slugify`,
 * which suggested `mqha-alqyq`. Both answer the wrong question — a slug is TRANSLATED, not
 * transliterated (see `apps/api/src/ai/slug.ts`). Keeping a local version "just in case" would mean
 * a second, worse implementation firing whenever the first is slow, which is exactly when nobody is
 * watching. The transliteration fallback still exists, once, on the server.
 */
